import { CvCard, type CvFile } from '@/components/CvCard'
import { CvUpload } from '@/components/CvUpload'
import { LanguageSelector } from '@/components/LanguageSelector.tsx'
import { ProfileEntries } from '@/components/ProfileEntries'
import { trackAuthenticatedOnce } from '@/trackAuthenticatedOnce'
import { logger } from '@jobilla/instrumentation'
import {
	candidacyIndexQueryKey,
	candidacyShowQueryKey,
	useCandidacyShow,
} from '@jobilla/nexus-sdk/lib/hooks/useCandidacy'
import { candidateProfileSection } from '@jobilla/nexus-sdk/lib/types/CandidateProfileSection'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, Navigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Centered } from '../components/Centered'

export const Route = createFileRoute('/organisations/$organisationId/$candidacyId/profile')({
	head: () => ({
		meta: [{ title: 'Jobilla' }],
	}),
	component: ProfileScreen,
})

function ProfileScreen() {
	const { organisationId, candidacyId } = Route.useParams()
	const { t } = useTranslation('common')
	const queryClient = useQueryClient()
	const candidacyQuery = useCandidacyShow(organisationId, candidacyId, { retry: 0 })
	const [isCvNew, setIsCvNew] = useState(false)

	const refreshCandidacy = () =>
		Promise.all([
			queryClient.invalidateQueries({ queryKey: candidacyShowQueryKey(organisationId, candidacyId) }),
			queryClient.invalidateQueries({ queryKey: candidacyIndexQueryKey(organisationId) }),
		])

	useEffect(() => {
		if (!candidacyQuery.isSuccess) {
			return
		}
		trackAuthenticatedOnce(candidacyId)
		logger.track('Candidate Center: Viewed profile', {
			data: {
				candidate_id: candidacyId,
				has_cv: candidacyQuery.data.profile.cv !== null,
				work_history_count: candidacyQuery.data.profile.workHistory.length,
				education_count: candidacyQuery.data.profile.education.length,
				licenses_count: candidacyQuery.data.profile.licenses.length,
			},
		})
		// Fire once per successful load — not again when an upload refetches the CV.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [candidacyQuery.isSuccess])

	if (candidacyQuery.isError) {
		const status = candidacyQuery.error.response?.status

		if (status === 401) {
			return <Navigate to="/" replace={true} />
		}

		return (
			<Centered>
				<p className="text-lg font-medium text-grey-800">
					{status === 404 ? t('submission.not_found.title') : t('login.error.title')}
				</p>
				<p className="max-w-sm text-sm text-grey-500">
					{status === 404 ? t('submission.not_found.body') : t('login.error.body')}
				</p>
				<Link
					to="/organisations/$organisationId"
					params={{ organisationId }}
					className="mt-4 text-sm font-medium text-brand-600 hover:text-brand-700"
				>
					{t('submission.back')}
				</Link>
			</Centered>
		)
	}

	if (candidacyQuery.isPending) {
		return (
			<Centered>
				<p className="text-sm text-grey-500">{t('login.loading')}</p>
			</Centered>
		)
	}

	const candidacy = candidacyQuery.data
	const { profile } = candidacy
	const cv: CvFile | null = profile.cv === null
		? null
		: { ...profile.cv, uploadedAt: new Date(profile.cv.uploadedAt) }

	return (
		<div className="mx-auto flex min-h-screen w-full max-w-md flex-col px-6 py-12">
			<header className="flex items-center justify-between gap-4">
				<Link
					to="/organisations/$organisationId/$candidacyId"
					params={{ organisationId, candidacyId }}
					className="text-sm font-medium text-grey-700 hover:text-grey-900"
				>
					‹ {t('profile.back')}
				</Link>
				<LanguageSelector />
			</header>

			<section className="mt-8">
				<p className="text-sm font-medium text-grey-500">
					{t('profile.context', {
						campaign: candidacy.campaignTitle,
						organisation: candidacy.organisation.name,
					})}
				</p>
				<h1 className="mt-1 text-xl font-semibold text-grey-900">{profile.name ?? t('profile.link.title')}</h1>
				<ul className="mt-3 flex flex-col gap-2 text-md text-grey-800">
					{profile.email === null
						? null
						: (
							<li className="flex items-center gap-2.5 break-all">
								<svg
									aria-hidden={true}
									viewBox="0 0 16 16"
									fill="none"
									stroke="currentColor"
									strokeWidth={1.5}
									strokeLinecap="round"
									strokeLinejoin="round"
									className="size-4 shrink-0 text-grey-400"
								>
									<rect x="2" y="3.5" width="12" height="9" rx="1.5" />
									<path d="M2.5 4.5L8 8.75l5.5-4.25" />
								</svg>
								{profile.email}
							</li>
						)}
					{profile.phone === null
						? null
						: (
							<li className="flex items-center gap-2.5">
								<svg
									aria-hidden={true}
									viewBox="0 0 16 16"
									fill="none"
									stroke="currentColor"
									strokeWidth={1.5}
									strokeLinecap="round"
									strokeLinejoin="round"
									className="size-4 shrink-0 text-grey-400"
								>
									<path d="M5.2 2.5H3.5a1 1 0 0 0-1 1.1c.5 5.3 4.6 9.4 9.9 9.9a1 1 0 0 0 1.1-1v-1.7a1 1 0 0 0-.8-1l-1.9-.4a1 1 0 0 0-.9.3l-.8.8a8 8 0 0 1-3.6-3.6l.8-.8a1 1 0 0 0 .3-.9l-.4-1.9a1 1 0 0 0-1-.8z" />
								</svg>
								{profile.phone}
							</li>
						)}
				</ul>
				{profile.canBeContacted
					? (
						<p className="mt-4 flex items-center gap-2 text-sm text-grey-500">
							<svg
								aria-hidden={true}
								viewBox="0 0 16 16"
								fill="none"
								stroke="currentColor"
								strokeWidth={1.8}
								strokeLinecap="round"
								strokeLinejoin="round"
								className="size-3.5 shrink-0 text-green-600"
							>
								<path d="M3.5 8.5l3 3 6-7" />
							</svg>
							{t('profile.contact_consent')}
						</p>
					)
					: null}
			</section>

			{cv !== null || candidacy.collectsCv
				? (
					<section aria-label={t('profile.cv.title')} className="mt-6">
						{cv === null
							? (
								<CvUpload
									organisationId={organisationId}
									candidacyId={candidacyId}
									onUploaded={() => {
										setIsCvNew(true)
										void refreshCandidacy()
									}}
								/>
							)
							: (
								<CvCard
									key={cv.id}
									cv={cv}
									isNew={isCvNew}
									organisationId={organisationId}
									candidacyId={candidacyId}
									onRemoved={() => {
										setIsCvNew(false)
										return refreshCandidacy()
									}}
								/>
							)}
					</section>
				)
				: null}

			{candidacy.profileEditable
				? (
					<>
						<section className="mt-8 border-t border-grey-200 pt-8">
							<ProfileEntries
								section={candidateProfileSection.work_history}
								organisationId={organisationId}
								candidacyId={candidacyId}
								entries={profile.workHistory}
								onChanged={refreshCandidacy}
							/>
						</section>

						<section className="mt-8 border-t border-grey-200 pt-8">
							<ProfileEntries
								section={candidateProfileSection.education}
								organisationId={organisationId}
								candidacyId={candidacyId}
								entries={profile.education}
								onChanged={refreshCandidacy}
							/>
						</section>

						<section className="mt-8 border-t border-grey-200 pt-8">
							<ProfileEntries
								section={candidateProfileSection.licenses}
								organisationId={organisationId}
								candidacyId={candidacyId}
								entries={profile.licenses}
								onChanged={refreshCandidacy}
							/>
						</section>
					</>
				)
				: null}
		</div>
	)
}
